/**
 * relicPadDisc — Ground disc so ARAM relics read on both ±X rails (SPY-302 / FID-MAP-01).
 */

export const RELIC_PAD_DISC_RADIUS = 1.15;
export const RELIC_PAD_DISC_HEIGHT = 0.08;
/** Pad glow stays below crystal PointLight so the disc does not wash the shrine (SPY-402). */
export const RELIC_PAD_EMISSIVE_SCALE = 0.35;
/** Inactive / on-cooldown pad dim vs active pulse (SPY-502). */
export const RELIC_PAD_INACTIVE_SCALE = 0.12;
/** Outer ring opacity for pad readability vs crystal (SPY-602). */
export const RELIC_PAD_RING_OPACITY = 0.28;
/** Disc fill opacity scale vs ring (SPY-1102 → SPY-1202). */
export const RELIC_PAD_DISC_OPACITY_SCALE = 0.44;
/** Inactive disc opacity floor (SPY-1102 → SPY-1202). */
export const RELIC_PAD_DISC_OPACITY_FLOOR = 0.02;
/** Active pad emissive pulse rate (FID-MAP-04). */
export const RELIC_PAD_PULSE_HZ = 1.3;
/** Peak ± boost on active emissive (FID-MAP-09). */
export const RELIC_PAD_PULSE_AMP = 0.3;

export function relicPadRingOpacity({ opacity = 1, active = true } = {}) {
  const scale = active ? 1 : 0.35;
  return Math.max(0.04, RELIC_PAD_RING_OPACITY * opacity * scale);
}

export function relicPadDiscOpacity({ opacity = 1, active = true } = {}) {
  const scale = active ? 1 : 0.35;
  return Math.max(
    RELIC_PAD_DISC_OPACITY_FLOOR,
    0.7 * RELIC_PAD_DISC_OPACITY_SCALE * opacity * scale
  );
}

/** 0..1 sine pulse for active pads; inactive stays 0 (FID-MAP-01). */
export function relicPadPulseFactor(tSec = 0, active = true) {
  if (!active) return 0;
  return 0.5 + 0.5 * Math.sin(2 * Math.PI * RELIC_PAD_PULSE_HZ * tSec);
}

export function relicPadEmissiveIntensity({
  opacity = 1,
  active = true,
  tSec = 0
} = {}) {
  const scale = active ? RELIC_PAD_EMISSIVE_SCALE : RELIC_PAD_INACTIVE_SCALE;
  const pulse = active
    ? 1 + RELIC_PAD_PULSE_AMP * Math.sin(2 * Math.PI * RELIC_PAD_PULSE_HZ * tSec)
    : 1;
  return Math.max(0.05, opacity * scale * pulse);
}

export function buildRelicPadDisc({
  MeshCls,
  CylinderGeo,
  MeshMat,
  relicId,
  color,
  opacity
}) {
  const geo = new CylinderGeo(
    RELIC_PAD_DISC_RADIUS,
    RELIC_PAD_DISC_RADIUS,
    RELIC_PAD_DISC_HEIGHT,
    16
  );
  const mat = new MeshMat({
    color,
    emissive: color,
    transparent: true,
    opacity,
    roughness: 0.7,
    emissiveIntensity: relicPadEmissiveIntensity({ opacity, active: true })
  });
  const mesh = new MeshCls(geo, mat);
  mesh.name = `relic-pad-${relicId}`;
  mesh.position.set(0, RELIC_PAD_DISC_HEIGHT / 2, 0);
  return { mesh, mat };
}

export function syncRelicPadDisc(entry, visual, tSec = 0) {
  if (!entry?.padMat || !visual) return;
  const active = visual.active !== false;
  entry._padActive = active;
  entry._padPulse = relicPadPulseFactor(tSec, active);
  entry.padMat.color = visual.color;
  entry.padMat.emissive = visual.color;
  entry.padMat.opacity = relicPadDiscOpacity({
    opacity: visual.opacity,
    active
  });
  entry.padMat.emissiveIntensity = relicPadEmissiveIntensity({
    opacity: visual.opacity,
    active,
    tSec
  });
}

export function summarizeRelicPadMeshes(entries = []) {
  return entries.map((e) => {
    const x = e.group?.position?.x ?? null;
    const active = e._padActive === true;
    const dim = e._padActive === false;
    return {
      relicId: e.relicId,
      hasPad: !!e.pad,
      padRadius: e.padRadius ?? e.pad?.geometry?.radiusTop ?? null,
      x,
      z: e.group?.position?.z ?? null,
      rail: x === null || x === undefined ? null : x < 0 ? '-X' : '+X',
      active,
      dim,
      pulseFactor: e._padPulse ?? null,
      emissiveIntensity: e.padMat?.emissiveIntensity ?? null,
      opacity: e.padMat?.opacity ?? null
    };
  });
}
